import { useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Alert, Box, Button, Icon, Text, TextSpinner } from '@factorialco/deprecated-design-system'
import {
  ImportProjectsTemplateDocument,
  ImportProjectsTemplateMutation,
  PreviewImportProjectsDocument,
  PreviewImportProjectsMutation,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { downloadBlob } from 'modules/core/lib/download'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import ImportDropzone from 'modules/project_management/components/Dropzone'

interface InfoComponentProps {
  message: string
}

interface ImportProjectsModalProps {
  onClose: () => void
}

const InfoComponent = ({ message }: InfoComponentProps) => {
  return (
    <Alert color="error">
      <Box alignItems="flexStart" flexDirection="row" gap="s12">
        <Box flexNoShrink>
          <Icon.Medium icon="alert-circle" color="grey800" />
        </Box>
        <Text.Normal>{message}</Text.Normal>
      </Box>
    </Alert>
  )
}

const ImportProjectsModal = ({ onClose }: ImportProjectsModalProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { state } = useLocation()
  const [getImportProjectsTemplate] = useMutation(ImportProjectsTemplateDocument, {
    extractorFn: (data: ImportProjectsTemplateMutation) =>
      data.projectManagementImporterMutations.getProjectmanagementimporterProjectsTemplate,
  })

  const fileDropped = useRef<File | undefined>(undefined)
  const [isIncorrectFile, setIsIncorrectFile] = useState<boolean>(false)
  const [incorrectFileMessage, setIncorrectFileMessage] = useState<string>('')
  const { fileDroppedFromDetailsPage } = state || { fileDroppedFromDetailsPage: null }

  const [previewImportProjects, { loading: loadingPreview }] = useMutation(
    PreviewImportProjectsDocument,
    {
      extractorFn: (data: PreviewImportProjectsMutation) =>
        data.projectManagementImporterMutations.previewProjectmanagementimporterProjectsImport,
    }
  )

  const download = async () => {
    const result = await getImportProjectsTemplate({
      variables: {},
    })

    const handled = handleResult(result, (_errors) => {
      // TODO: handle errors properly
    })

    if (handled.success) {
      if (!handled.data.projectsTemplate) return

      // TODO: refactor this, we had no alternative way
      const b = atob(handled.data.projectsTemplate.stream)
      const byteArray = new Uint8Array(b.length)

      for (let i = 0; i < b.length; i++) {
        byteArray[i] = b.charCodeAt(i)
      }

      const blob = new Blob([byteArray], {
        type: handled.data.projectsTemplate.fileType,
      })

      downloadBlob(blob, handled.data.projectsTemplate.fileName)
      track(trackingEvents.projectManagement.importProjects.downloadImportProjectsTemplate)
    }
  }

  const onDropFile = (files: File[]) => {
    const file = files[0]
    if (!file) {
      track(trackingEvents.projectManagement.importProjects.invalidFileFormatUploaded)
      setIncorrectFileMessage(
        i18n.t(
          'project_management.imports.modals.import_projects_data.incorrect_file_format_message'
        )
      )
      setIsIncorrectFile(true)
      return
    }

    fileDropped.current = file

    track(trackingEvents.projectManagement.importProjects.uploadFileForProjectImport)
    setIsIncorrectFile(false)
  }
  const onSubmit = async () => {
    track(trackingEvents.projectManagement.importProjects.importProjectsSubmit)
    if (!fileDropped.current || fileDropped.current === undefined) return null
    const result = await previewImportProjects({
      variables: {
        file: fileDropped.current,
      },
    })

    const handled = handleResult(result, (error) => {
      if (error.validation) {
        track(trackingEvents.projectManagement.importProjects.emptyFileUploaded)
        setIncorrectFileMessage(error.validation)
        setIsIncorrectFile(true)
      }
    })
    if (handled.success) {
      track(trackingEvents.projectManagement.importProjects.visitImportProjectsDetailsPage)
      navigate(navigation.projects.importDetails.fullPath(), {
        state: {
          token: handled.data.projectsImport?.token,
          projects: handled.data.projectsImport?.projects,
        },
      })
    }
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('project_management.imports.modals.import_projects_data.title')}
      description={i18n.t('project_management.imports.modals.import_projects_data.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit>
            {i18n.t('project_management.imports.modals.import_projects_data.submit')}
          </Button>
        </>
      }
    >
      {loadingPreview ? (
        <TextSpinner
          text={i18n.t('project_management.imports.modals.import_projects_data.spinner_message')}
        />
      ) : (
        <Box gap="s24">
          <Box
            cursor="pointer"
            padding="s16"
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceBetween"
            onClick={download}
            borderRadius={{ all: 'abs006' }}
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            <Text size="200">
              {i18n.t('project_management.imports.modals.import_projects_data.download_template')}
            </Text>
            <Box
              padding="s2"
              borderRadius={{
                all: 'rel1000',
              }}
              border={{
                all: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
            >
              <Icon.Medium icon="download" color="grey600" />
            </Box>
          </Box>
          <>
            <Text.Normal>
              {i18n.t(
                'project_management.imports.modals.import_projects_data.drag_and_drop_message'
              )}
            </Text.Normal>
            <ImportDropzone
              fileDroppedFromDetailsPage={fileDroppedFromDetailsPage}
              isIncorrectFile={isIncorrectFile}
              label={i18n.tView(
                'project_management.imports.modals.import_projects_data.drag_drop_placeholder',
                {
                  browse: (
                    <Text.Normal color="viridian1000" weight="medium">
                      {i18n.t('project_management.imports.browse')}{' '}
                    </Text.Normal>
                  ),
                }
              )}
              onDropFile={onDropFile}
            />
          </>
          {isIncorrectFile && <InfoComponent message={incorrectFileMessage} />}
        </Box>
      )}
    </Modal>
  )
}

export default ImportProjectsModal
